import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser, useAuth as useClerkAuth, useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useAuth } from '../contexts/AuthProvider';
import logo from '../assets/logo.png';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import ReactCountryFlag from 'react-country-flag';

const languages = [
  { code: 'en', name: 'English', flagCode: 'GB' },
  { code: 'fr', name: 'Français', flagCode: 'FR' },
  { code: 'nl', name: 'Nederlands', flagCode: 'NL' },
  { code: 'de', name: 'Deutsch', flagCode: 'DE' },
  { code: 'es', name: 'Español', flagCode: 'ES' },
  { code: 'it', name: 'Italiano', flagCode: 'IT' },
];

/**
 * Login page component
 * Handles Clerk authentication and syncs with backend
 */
const Login = () => {
  const { t, i18n } = useTranslation('login');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn, user: clerkUser, isLoaded } = useUser();
  const { getToken } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  const { signIn, setActive } = useSignIn();
  const { signUp } = useSignUp();
  const { user: appUser, setUser, loading: authLoading } = useAuth();
  const [errorKey, setErrorKey] = useState(''); // Store translation key, not translated string
  const [syncing, setSyncing] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'microsoft' | null
  const [accessDenied, setAccessDenied] = useState(false); // Track if user was denied access

  // Email OTP state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false); // Track if we're doing sign-up vs sign-in

  // Check if user wants to switch account (via ?switch=true parameter)
  const wantToSwitch = searchParams.get('switch') === 'true';

  // Check for Clerk OAuth errors in URL (Clerk adds these after failed OAuth)
  const clerkError = searchParams.get('__clerk_status');
  const clerkErrorMessage = searchParams.get('__clerk_error');

  // Clean up URL params from Clerk on mount
  useEffect(() => {
    // Check for Clerk OAuth errors
    if (clerkError || clerkErrorMessage) {
      sessionStorage.removeItem('pricy_oauth_pending');
      setErrorKey('failedToSignIn');
      navigate('/login', { replace: true });
      return;
    }

    // Clean up Clerk handshake params from URL
    if (searchParams.has('__clerk_handshake')) {
      navigate('/login', { replace: true });
    }
  }, [clerkError, clerkErrorMessage, searchParams, navigate, t]);

  // Detect failed OAuth: we were expecting an OAuth callback but Clerk says not signed in
  useEffect(() => {
    const oauthPending = sessionStorage.getItem('pricy_oauth_pending');
    console.log('[Login] OAuth pending check:', { isLoaded, isSignedIn, oauthPending, clerkUserEmail: clerkUser?.primaryEmailAddress?.emailAddress });

    if (isLoaded && oauthPending) {
      // Clear the pending flag
      sessionStorage.removeItem('pricy_oauth_pending');

      if (!isSignedIn) {
        // We returned from OAuth but no session was created
        // This means the OAuth failed silently (user not in Clerk, or error)
        console.log('[Login] OAuth failed - no session created');
        setErrorKey('accessDenied');
      } else {
        console.log('[Login] OAuth success - session created, will sync with backend');
      }
      // If isSignedIn is true, the syncWithBackend useEffect will handle it
    }
  }, [isLoaded, isSignedIn, clerkUser, t]);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  // Handle OAuth sign in - signs out first to force account selection
  const handleOAuthSignIn = async (strategy) => {
    setOauthLoading(strategy);
    setErrorKey('');
    setAccessDenied(false); // Reset access denied state for new attempt
    try {
      // Sign out first to clear any existing session (forces account picker)
      await clerkSignOut();

      // Set a flag so we know we're expecting an OAuth callback
      sessionStorage.setItem('pricy_oauth_pending', 'true');

      const oauthStrategy = strategy === 'google' ? 'oauth_google' : 'oauth_microsoft';

      // Use signIn for OAuth - works for existing users
      // For new users, Clerk will automatically handle user creation via "transfer" flow
      await signIn?.authenticateWithRedirect({
        strategy: oauthStrategy,
        redirectUrl: window.location.origin + '/login',
        redirectUrlComplete: window.location.origin + '/login',
      });
    } catch (err) {
      console.error('OAuth error:', err);
      sessionStorage.removeItem('pricy_oauth_pending');
      setErrorKey('failedToSignIn');
      setOauthLoading(null);
    }
  };

  // Handle email OTP - Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setEmailLoading(true);
    setErrorKey('');

    try {
      // Check if email is authorized before sending OTP
      const checkResponse = await axiosInstance.post('/api/auth/check-email/', {
        email: email.trim(),
      });

      if (!checkResponse.data.allowed) {
        setErrorKey('emailNotAuthorized');
        setEmailLoading(false);
        return;
      }

      // Sign out first to clear any existing session
      await clerkSignOut();

      // Try sign-in first (for existing Clerk users)
      try {
        const result = await signIn?.create({
          identifier: email.trim(),
        });

        // Prepare email code verification
        await result?.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: result.supportedFirstFactors?.find(
            (f) => f.strategy === 'email_code'
          )?.emailAddressId,
        });

        setIsSignUp(false);
        setOtpSent(true);
      } catch (signInErr) {
        // If user doesn't exist in Clerk, try sign-up (this is expected for new users)
        if (signInErr.errors?.[0]?.code === 'form_identifier_not_found') {
          const signUpResult = await signUp?.create({
            emailAddress: email.trim(),
          });

          // Prepare email code verification for sign-up
          await signUpResult?.prepareEmailAddressVerification({
            strategy: 'email_code',
          });

          setIsSignUp(true);
          setOtpSent(true);
        } else {
          throw signInErr;
        }
      }
    } catch (err) {
      console.error('Email OTP error:', err);
      setErrorKey('failedToSignIn');
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle email OTP - Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) return;

    setEmailLoading(true);
    setErrorKey('');

    try {
      let result;

      if (isSignUp) {
        // Verify sign-up email
        result = await signUp?.attemptEmailAddressVerification({
          code: otpCode.trim(),
        });

        if (result?.status === 'complete') {
          await setActive?.({ session: result.createdSessionId });
          // The useEffect will handle syncing with backend
        } else {
          setErrorKey('failedToVerify');
        }
      } else {
        // Verify sign-in
        result = await signIn?.attemptFirstFactor({
          strategy: 'email_code',
          code: otpCode.trim(),
        });

        if (result?.status === 'complete') {
          await setActive?.({ session: result.createdSessionId });
          // The useEffect will handle syncing with backend
        } else {
          setErrorKey('failedToVerify');
        }
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      const errorCode = err.errors?.[0]?.code;

      if (errorCode === 'form_code_incorrect') {
        setErrorKey('invalidCode');
      } else if (errorCode === 'verification_already_verified') {
        // Verification was already completed - try to activate the session
        try {
          if (isSignUp && signUp?.createdSessionId) {
            await setActive?.({ session: signUp.createdSessionId });
          } else if (!isSignUp && signIn?.createdSessionId) {
            await setActive?.({ session: signIn.createdSessionId });
          } else {
            // Session ID not available, user may need to retry
            setErrorKey('failedToVerify');
          }
        } catch (activateErr) {
          console.error('Failed to activate session after already verified:', activateErr);
          setErrorKey('failedToVerify');
        }
      } else {
        setErrorKey('failedToVerify');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // Resend OTP code
  const handleResendCode = async () => {
    setEmailLoading(true);
    setErrorKey('');
    setOtpCode('');

    try {
      if (isSignUp) {
        // For sign-up, we need to restart the process if already verified
        // First check if we can just prepare again
        try {
          await signUp?.prepareEmailAddressVerification({
            strategy: 'email_code',
          });
          setErrorKey('codeResent');
        } catch (prepareErr) {
          // If already verified, try to complete the sign-up
          if (prepareErr.errors?.[0]?.code === 'verification_already_verified') {
            // Check if sign-up is complete
            if (signUp?.status === 'complete' && signUp?.createdSessionId) {
              await setActive?.({ session: signUp.createdSessionId });
              return;
            }
            // Otherwise restart the sign-up process
            await clerkSignOut();
            const newSignUp = await signUp?.create({
              emailAddress: email.trim(),
            });
            await newSignUp?.prepareEmailAddressVerification({
              strategy: 'email_code',
            });
            setErrorKey('codeResent');
          } else {
            throw prepareErr;
          }
        }
      } else {
        // Resend for sign-in
        await signIn?.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: signIn.supportedFirstFactors?.find(
            (f) => f.strategy === 'email_code'
          )?.emailAddressId,
        });
        setErrorKey('codeResent');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setErrorKey('failedToResend');
    } finally {
      setEmailLoading(false);
    }
  };

  // Reset email form
  const handleBackToMethods = () => {
    setShowEmailForm(false);
    setOtpSent(false);
    setIsSignUp(false);
    setEmail('');
    setOtpCode('');
    setErrorKey('');
    setAccessDenied(false); // Reset access denied state
  };

  // If user is signed in but wants to switch accounts, sign them out first
  useEffect(() => {
    if (isLoaded && isSignedIn && wantToSwitch && !signingOut) {
      setSigningOut(true);
      clerkSignOut().then(() => {
        setSigningOut(false);
        // Remove the switch parameter from URL
        navigate('/login', { replace: true });
      });
    }
  }, [isLoaded, isSignedIn, wantToSwitch, signingOut, clerkSignOut, navigate]);

  // Sync Clerk user with backend after sign-in
  useEffect(() => {
    const syncWithBackend = async () => {
      console.log('[Login] syncWithBackend check:', { isSignedIn, clerkUser: !!clerkUser, syncing, accessDenied, isLoaded });
      // Don't sync if already denied access (prevents re-triggering)
      if (!isSignedIn || !clerkUser || syncing || accessDenied) {
        console.log('[Login] syncWithBackend skipped:', { isSignedIn, hasClerkUser: !!clerkUser, syncing, accessDenied });
        return;
      }

      setSyncing(true);
      setErrorKey('');

      try {
        // Get Clerk JWT token
        const token = await getToken();
        console.log('[Login] Got Clerk token, sending to backend. Email:', clerkUser.primaryEmailAddress?.emailAddress);

        // Send to backend to verify and create/get PricyUser
        const response = await axiosInstance.post('/api/auth/clerk/', {
          clerk_token: token,
          email: clerkUser.primaryEmailAddress?.emailAddress,
          clerk_user_id: clerkUser.id,
          first_name: clerkUser.firstName,
          last_name: clerkUser.lastName,
          avatar_url: clerkUser.imageUrl,
        });

        console.log('[Login] Backend response:', response.data);
        if (response.data.allowed) {
          // Store the backend JWT tokens
          localStorage.setItem('accessToken', response.data.access);
          localStorage.setItem('refreshToken', response.data.refresh);
          localStorage.setItem('userDetails', JSON.stringify(response.data.user));

          // Update app user state
          setUser(response.data.user);

          // Redirect to products
          navigate('/products', { replace: true });
        } else {
          // Access denied - just show error, don't sign out yet
          // Sign out will happen when user clicks OAuth button to try again
          setAccessDenied(true);
          setErrorKey('accessDenied');
          setSyncing(false);
        }
      } catch (err) {
        console.error('[Login] Backend sync error:', err);
        // Check if it's an access denied error from backend
        if (err.response?.status === 403 || err.response?.data?.error?.includes('not allowed')) {
          // Access denied - just show error, don't sign out yet
          setAccessDenied(true);
          setErrorKey('accessDenied');
          setSyncing(false);
        } else {
          setErrorKey('failedToVerify');
          setSyncing(false);
        }
      }
    };

    if (isLoaded && isSignedIn) {
      syncWithBackend();
    }
  }, [isSignedIn, clerkUser, isLoaded, accessDenied, syncing, getToken, navigate, setUser, t]);

  // If auth is still loading, show nothing (prevents flash to login page on refresh)
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If already logged in with app auth, redirect
  if (appUser) {
    return <Navigate to="/products" replace />;
  }

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      {/* Language selector in top right */}
      <div className="absolute top-4 right-4">
        <Select value={i18n.language} onValueChange={handleLanguageChange}>
          <SelectTrigger className="w-[160px] bg-card">
            <SelectValue>
              <span className="flex items-center gap-2">
                <ReactCountryFlag
                  countryCode={currentLang.flagCode}
                  svg
                  style={{ width: '1.25em', height: '1em', verticalAlign: 'middle' }}
                />
                <span>{currentLang.name}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                <span className="flex items-center gap-2">
                  <ReactCountryFlag
                    countryCode={lang.flagCode}
                    svg
                    style={{ width: '1.25em', height: '1em', verticalAlign: 'middle' }}
                  />
                  <span>{lang.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-48 h-48 flex items-center justify-center">
            <img
              src={logo}
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>
        </CardHeader>

        <CardContent className="flex flex-col items-center">
          {errorKey && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md mb-4 w-full text-center">
              {t(errorKey)}
            </div>
          )}

          {signingOut ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('signingOut')}</p>
            </div>
          ) : syncing ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('verifyingAccess')}</p>
            </div>
          ) : isSignedIn && !wantToSwitch && !accessDenied ? (
            // User is signed in with Clerk but not yet synced with backend - show option to use different account
            <div className="text-center py-4 w-full space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('signedInAs')} <span className="font-medium text-foreground">{clerkUser?.primaryEmailAddress?.emailAddress}</span>
              </p>
              <button
                onClick={() => navigate('/login?switch=true')}
                className="text-sm text-primary hover:underline"
              >
                {t('useDifferentAccount')}
              </button>
            </div>
          ) : showEmailForm ? (
            // Email OTP form
            <div className="w-full space-y-4">
              {!otpSent ? (
                // Step 1: Enter email
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Input
                      type="email"
                      placeholder={t('enterEmail')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={emailLoading}
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={emailLoading || !email.trim()}
                  >
                    {emailLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      t('sendCode')
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={handleBackToMethods}
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    {t('backToMethods')}
                  </button>
                </form>
              ) : (
                // Step 2: Enter OTP code
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="text-center space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {t('codeSentTo')} <span className="font-medium text-foreground">{email}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('checkSpam')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="text"
                      placeholder={t('enterCode')}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      disabled={emailLoading}
                      autoFocus
                      maxLength={6}
                      className="text-center text-lg tracking-widest"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={emailLoading || !otpCode.trim()}
                  >
                    {emailLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      t('verifyCode')
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={emailLoading}
                    className="w-full text-sm text-primary hover:underline disabled:opacity-50"
                  >
                    {t('resendCode')}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToMethods}
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    {t('backToMethods')}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div className="w-full space-y-3">
              {/* OAuth buttons */}
              <button
                onClick={() => handleOAuthSignIn('google')}
                disabled={oauthLoading !== null}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading === 'google' ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span className="text-sm font-medium">{t('continueWithGoogle')}</span>
              </button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('or')}</span>
                </div>
              </div>

              {/* Email OTP button */}
              <button
                onClick={() => setShowEmailForm(true)}
                disabled={oauthLoading !== null}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium">{t('continueWithEmail')}</span>
              </button>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
